/**
 * Plain-language summaries of EI tool_trace entries so operators can see
 * what the agent is reading/searching while "Gathering more evidence".
 */
import type { EiToolInvocation } from '../types/engineeringIntelligence';

function asString(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function shortPath(path: string): string {
  const p = path.replace(/\\/g, '/');
  if (p.length <= 72) return p;
  const parts = p.split('/');
  if (parts.length <= 3) return `…${p.slice(-69)}`;
  return `…/${parts.slice(-3).join('/')}`;
}

/** One-line description of a single tool invocation, or null if empty. */
export function describeEiToolInvocation(inv: EiToolInvocation | null | undefined): string | null {
  if (!inv) return null;
  const tool = asString(inv.call?.tool);
  const args = inv.call?.args || {};
  const path = asString(args.path || args.file || args.filepath || args.target);
  const query = asString(args.query || args.pattern || args.q || args.search);
  const low = tool.toLowerCase();

  if ((low.includes('read_file') || low.includes('read')) && path) {
    return `Reading ${shortPath(path)}`;
  }
  if (low.includes('search') || low.includes('grep')) {
    if (query && path) return `Searching “${query.slice(0, 48)}” in ${shortPath(path)}`;
    if (query) return `Searching “${query.slice(0, 64)}”`;
    if (path) return `Searching ${shortPath(path)}`;
  }
  if (low.includes('list') && path) return `Listing ${shortPath(path)}`;
  if (path && tool) return `${tool} → ${shortPath(path)}`;
  if (path) return shortPath(path);

  const summary = asString(inv.result_summary);
  if (summary) {
    return summary.length > 96 ? `${summary.slice(0, 93)}…` : summary;
  }
  if (tool) return tool;
  return null;
}

/** Most recent meaningful tool activity (newest first). */
export function latestEiToolActivity(
  toolTrace: EiToolInvocation[] | null | undefined,
): string | null {
  if (!toolTrace?.length) return null;
  for (let i = toolTrace.length - 1; i >= 0; i -= 1) {
    const line = describeEiToolInvocation(toolTrace[i]);
    if (line) return line;
  }
  return null;
}

/**
 * Tool activity near a transition timestamp — used to annotate
 * "Gathering more evidence" timeline rows with the files being read.
 */
export function eiToolActivityNear(
  toolTrace: EiToolInvocation[] | null | undefined,
  atIso: string | undefined,
  opts?: { windowMs?: number; limit?: number },
): string[] {
  if (!toolTrace?.length) return [];
  const windowMs = opts?.windowMs ?? 120_000;
  const limit = opts?.limit ?? 3;
  const at = atIso ? Date.parse(atIso) : NaN;
  const ranked = [...toolTrace].reverse();
  const out: string[] = [];
  const seen = new Set<string>();

  for (const inv of ranked) {
    const line = describeEiToolInvocation(inv);
    if (!line || seen.has(line)) continue;
    if (Number.isFinite(at) && inv.at) {
      const t = Date.parse(inv.at);
      if (Number.isFinite(t) && Math.abs(t - at) > windowMs) continue;
    }
    seen.add(line);
    out.push(line);
    if (out.length >= limit) break;
  }

  // If timestamp window filtered everything out, fall back to latest tools.
  if (out.length === 0) {
    for (const inv of ranked) {
      const line = describeEiToolInvocation(inv);
      if (!line || seen.has(line)) continue;
      seen.add(line);
      out.push(line);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Distinct file paths the agent touched near a transition timestamp,
 * newest first — for file chips on timeline rows. */
export function eiFilesNear(
  toolTrace: EiToolInvocation[] | null | undefined,
  atIso: string | undefined,
  opts?: { windowMs?: number; limit?: number },
): string[] {
  if (!toolTrace?.length) return [];
  const windowMs = opts?.windowMs ?? 120_000;
  const limit = opts?.limit ?? 3;
  const at = atIso ? Date.parse(atIso) : NaN;
  const out: string[] = [];
  for (let i = toolTrace.length - 1; i >= 0 && out.length < limit; i -= 1) {
    const inv = toolTrace[i];
    const args = inv?.call?.args || {};
    const path = asString(args.path || args.file || args.filepath || args.target).replace(/\\/g, '/');
    if (!path || out.includes(path)) continue;
    if (Number.isFinite(at)) {
      const t = inv.at ? Date.parse(inv.at) : NaN;
      if (!Number.isFinite(t) || Math.abs(t - at) > windowMs) continue;
    }
    out.push(path);
  }
  return out;
}

const GATHER_STATES = new Set([
  'GATHERING_MORE_EVIDENCE',
  'COLLECTING_EVIDENCE',
  'FORMING_HYPOTHESES',
  'ADMITTING',
  'ADMITTED',
  'NEW_INCIDENT',
]);

export function isEiGatheringState(state?: string | null): boolean {
  return GATHER_STATES.has(String(state || '').toUpperCase());
}
